import React from "react";
import { useApp } from "../state/AppContext";
import { DIFFICULTY_STYLES, localizedDifficulty } from "../data/localize";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

export default function DetailScreen() {
  const { detailChallenge, setScreen, setCurrentChallenge, t, lang } = useApp();
  const c = detailChallenge;

  if (!c) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center bg-scl-cream" data-testid="detail-empty">
        <p className="text-scl-charcoal/70 mb-4">—</p>
        <button onClick={() => setScreen("gallery")} className="h-12 px-6 rounded-full bg-scl-coral text-white font-semibold min-h-[44px]" data-testid="back-to-gallery">
          {t("empty_browse")}
        </button>
      </div>
    );
  }
  const d = DIFFICULTY_STYLES[c.difficulty];
  const accept = () => { setCurrentChallenge(c); setScreen("submit"); };

  return (
    <div className="min-h-screen bg-scl-cream pb-48" data-testid="detail-screen">
      <div className="relative h-64">
        <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-scl-cream via-scl-charcoal/20 to-transparent" />
        <button
          onClick={() => setScreen("gallery")}
          data-testid="back-btn"
          className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-warm-sm hover:bg-white active:scale-95 min-h-[44px]"
        >
          <ArrowLeft className="w-5 h-5 text-scl-charcoal" />
        </button>
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${d.bg} ${d.text}`} data-testid="detail-difficulty">
            {localizedDifficulty(c.difficulty, lang)}
          </span>
          <span className="px-3 py-1 rounded-full bg-scl-mustard text-scl-charcoal text-[11px] font-bold" data-testid="detail-points">
            {c.points} pts
          </span>
        </div>
      </div>

      <div className="px-6 -mt-6 relative">
        <div className="mb-3">
          <span className="inline-block px-3 py-1 rounded-full bg-white text-scl-charcoal text-[11px] font-semibold uppercase tracking-wider shadow-warm-sm" data-testid="detail-age">
            {c.age_display || c.age}
          </span>
        </div>
        <h1 className="font-serif text-3xl leading-tight text-scl-charcoal font-bold" data-testid="detail-title">{c.title}</h1>
        <p className="mt-3 text-[15px] text-scl-charcoal/80 leading-relaxed">{c.description}</p>

        <div className="mt-6 bg-white rounded-2xl p-5 shadow-warm-sm">
          <div className="text-xs uppercase tracking-widest text-scl-teal font-semibold mb-3">{t("detail_how")}</div>
          <ul className="space-y-3">
            {c.steps.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`step-${i}`}>
                <div className="mt-0.5 w-6 h-6 rounded-full bg-scl-teal/10 text-scl-teal font-bold text-xs flex items-center justify-center flex-shrink-0">{i + 1}</div>
                <div className="text-[15px] text-scl-charcoal leading-relaxed">{s}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-5 p-4 bg-scl-mustard/40 border border-scl-mustard/60 rounded-2xl flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-scl-teal" />
          </div>
          <div className="text-[13px] text-scl-charcoal/80">{t("detail_reminder")}</div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
        <div className="max-w-[480px] mx-auto px-6 pb-24 pt-6 bg-gradient-to-t from-scl-cream via-scl-cream to-scl-cream/0 pointer-events-auto">
          <button
            onClick={accept}
            data-testid="detail-accept-btn"
            className="w-full h-14 rounded-full bg-scl-coral text-white font-semibold text-lg flex items-center justify-center gap-2 shadow-warm hover:brightness-105 active:scale-[0.98] transition-all min-h-[44px]"
          >
            {t("detail_accept")} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
